import React from 'react'

import CurrencyLogo from '@/components/CurrencyLogo'
import { nutrimaticLabels } from '@/nutrimatic'
import { cn } from '@/lib/utils'
import { useIsOfficialToken, useIsUserAddedToken } from '@/hooks/Tokens'
import { Currency, CurrencyAmount, JSBI, Token } from '@/sdk'

import { ImportedTokenBadge } from './ImportedTokenBadge'
import { ImportedTokenRemoveButton } from './ImportedTokenRemoveButton'

const labels = nutrimaticLabels.mobileSimple

export function TokenPickerSelectionRoleBadge({ role }: { role: 'pay' | 'receive' }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        role === 'pay'
          ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
          : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      )}
    >
      {role === 'pay' ? 'Pay' : 'Receive'}
    </span>
  )
}

/** Fila de token reutilizable: badge «Importado» + quitar en tokens del usuario. */
export function TokenPickerListRow({
  currency,
  balance,
  onSelect,
  selected,
  disabled,
  selectionRole,
  className,
  balancePrecision = 6,
}: {
  currency: Currency
  balance?: CurrencyAmount
  onSelect: (currency: Currency) => void
  selected?: boolean
  disabled?: boolean
  selectionRole?: 'pay' | 'receive'
  className?: string
  balancePrecision?: number
}) {
  const isUserAdded = useIsUserAddedToken(currency)
  const isOfficial = useIsOfficialToken(currency)
  const symbol = currency.symbol ?? '—'
  const canRemove = isUserAdded && !isOfficial && currency instanceof Token
  const hasBalance = balance?.greaterThan(JSBI.BigInt(0))

  const trailing = (
    <span className="nm-mobile-simple__picker-row-trailing !flex-row !items-center gap-2">
      {selectionRole ? <TokenPickerSelectionRoleBadge role={selectionRole} /> : null}
      {hasBalance && balance ? (
        <span className="nm-mobile-simple__picker-row-primary">
          {labels.pickerBalanceAmount(balance.toSignificant(balancePrecision), symbol)}
        </span>
      ) : null}
    </span>
  )

  const rowBody = (
    <>
      <span className="nm-mobile-simple__picker-row-leading">
        <CurrencyLogo currency={currency} size="40px" />
        <span className="nm-mobile-simple__picker-row-copy">
          <span className={canRemove ? 'nm-mobile-simple__picker-row-symbol-line' : undefined}>
            <span className="nm-mobile-simple__picker-row-symbol">{symbol}</span>
            {canRemove ? <ImportedTokenBadge /> : null}
          </span>
          <span className="nm-mobile-simple__picker-row-name">{currency.name ?? symbol}</span>
        </span>
      </span>
      {trailing}
    </>
  )

  if (!canRemove) {
    return (
      <div className={cn('nm-mobile-simple__picker-row-wrap', className)}>
        <button
          type="button"
          className={cn(
            'nm-mobile-simple__picker-row w-full',
            selected && 'border border-primary/40 bg-primary/10',
            disabled && 'cursor-not-allowed opacity-40',
          )}
          disabled={disabled}
          onClick={() => onSelect(currency)}
        >
          {rowBody}
        </button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'nm-mobile-simple__picker-row-wrap nm-mobile-simple__picker-row-wrap--imported',
        selected && 'border-primary/40 ring-1 ring-primary/25',
        className,
      )}
    >
      <button
        type="button"
        className={cn('nm-mobile-simple__picker-row min-w-0 flex-1', disabled && 'cursor-not-allowed opacity-40')}
        disabled={disabled}
        onClick={() => onSelect(currency)}
      >
        {rowBody}
      </button>
      <div className="nm-mobile-simple__picker-row-actions">
        <ImportedTokenRemoveButton token={currency} />
      </div>
    </div>
  )
}
